import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Chip,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Close as CloseIcon,
  Explore as ServiceIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import servicesService, {
  Service,
  ServiceCreate,
  MedicationType,
  MEDICATION_TYPE_LABELS,
} from '../services/servicesService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { serviceColumns } from '../config/serviceColumns';

type ServiceForm = Pick<ServiceCreate, 'name' | 'medication_type'>;

// One row of the ordered meeting flow in the dialog (drag handle + remove).
const FlowRow: React.FC<{ meetingType: MeetingType; index: number; onRemove: () => void }> = ({ meetingType, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: meetingType.id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
        border: '1px solid', borderColor: 'divider', borderRadius: 1,
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
        transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1,
      }}
    >
      <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
        <DragIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ minWidth: 20, color: 'text.secondary' }}>{index + 1}.</Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>{meetingType.name}</Typography>
      <Chip
        label={meetingType.only_once ? 'once' : 'repeatable'}
        size="small"
        variant="outlined"
        color={meetingType.only_once ? 'default' : 'warning'}
      />
      <IconButton size="small" onClick={onRemove}><CloseIcon fontSize="small" /></IconButton>
    </Box>
  );
};

const ServicesPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [flowIds, setFlowIds] = useState<number[]>([]);

  const { register, handleSubmit, formState: { errors }, reset, setValue, control } = useForm<ServiceForm>();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const meetingTypeById = useMemo(() => {
    const map = new Map<number, MeetingType>();
    meetingTypes.forEach((mt) => map.set(mt.id, mt));
    return map;
  }, [meetingTypes]);

  const flowLabel = useCallback(
    (ids: number[]) => ids.map((id) => meetingTypeById.get(id)?.name ?? `#${id}`).join(' → '),
    [meetingTypeById],
  );

  const getSortValue = useCallback((service: Service, key: string): any => {
    switch (key) {
      case 'name': return service.name.toLowerCase();
      case 'medicationType': return service.medication_type;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((service: Service): string[] => {
    return [service.name, MEDICATION_TYPE_LABELS[service.medication_type], flowLabel(service.meeting_type_ids)];
  }, [flowLabel]);

  const table = useTablePage<Service>({
    entityType: 'services',
    columns: serviceColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const loadServices = useCallback(async () => {
    if (!currentTenant) return;
    table.loadData(() => servicesService.getAll(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  useEffect(() => {
    if (currentTenant) {
      loadServices();
      meetingTypeService.getMeetingTypes(currentTenant.id).then(setMeetingTypes).catch(() => {});
    }
  }, [currentTenant, loadServices]);

  const handleOpenDialog = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setValue('name', service.name);
      setValue('medication_type', service.medication_type);
      setFlowIds(service.meeting_type_ids || []);
    } else {
      setEditingService(null);
      reset({ name: '', medication_type: 'IL' });
      setFlowIds([]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingService(null);
    reset();
    setFlowIds([]);
  };

  const crud = useCrudHandlers<Service>({
    table,
    createFn: (data) => servicesService.create(currentTenant!.id, data as ServiceCreate),
    updateFn: (id, data) => servicesService.update(id as number, data),
    deleteFn: (id) => servicesService.delete(id as number),
    entityName: 'Service',
    onSuccess: () => { handleCloseDialog(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setServiceToDelete(null); },
    reloadData: loadServices,
  });

  const onSubmit = (data: ServiceForm) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }
    crud.handleCreateOrUpdate({ ...data, meeting_type_ids: flowIds }, editingService);
  };

  const handleFlowSelect = (selected: number[]) => {
    // Keep existing order; append newly selected at the end, drop unselected.
    setFlowIds((prev) => [...prev.filter((id) => selected.includes(id)), ...selected.filter((id) => !prev.includes(id))]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFlowIds((prev) => arrayMove(prev, prev.indexOf(Number(active.id)), prev.indexOf(Number(over.id))));
    }
  };

  const getColumnValue = (service: Service, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <ServiceIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{service.name}</Typography>
          </Box>
        );
      case 'medicationType':
        return <Chip label={MEDICATION_TYPE_LABELS[service.medication_type]} color="primary" variant="outlined" size="small" />;
      case 'meetingFlow':
        return service.meeting_type_ids.length > 0
          ? <Typography variant="body2">{flowLabel(service.meeting_type_ids)}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>;
      default: return '';
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: () => { handleOpenDialog(table.selectedItem!); table.handleMenuClose(); } },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: () => { setServiceToDelete(table.selectedItem!); setDeleteDialogOpen(true); table.handleMenuClose(); }, color: 'error' as const },
    ];
  };

  const renderMobileCard = (service: Service) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center">
          <ServiceIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{service.name}</Typography>
        </Box>
        <Chip label={MEDICATION_TYPE_LABELS[service.medication_type]} color="primary" variant="outlined" size="small" />
      </Box>
      <Typography variant="body2" color="text.secondary">{flowLabel(service.meeting_type_ids) || '—'}</Typography>
    </>
  );

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="services">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading services:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Services"
          searchPlaceholder="Search services..."
          actions={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ whiteSpace: 'nowrap' }}>
              Add Service
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={table.sortedData}
            renderCard={renderMobileCard}
            getItemKey={(s) => s.id}
            onMenuClick={table.handleRowClick}
            emptyIcon={<ServiceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No services found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={table.sortedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              getRowKey={(s) => s.id}
              onRowClick={table.handleRowClick}
            />
          </DataTableContainer>
        )}

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        <ClinicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editingService ? 'Edit Service' : 'Create Service'}
          maxWidth="sm"
          onSubmit={handleSubmit(onSubmit)}
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingService ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
            </>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField
                {...register('name', { required: 'Name is required' })}
                fullWidth size="small"
                error={!!errors.name} helperText={errors.name?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Medication Type</Typography>
              <Controller
                name="medication_type"
                control={control}
                defaultValue="IL"
                rules={{ required: 'Medication type is required' }}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    {(Object.keys(MEDICATION_TYPE_LABELS) as MedicationType[]).map((mt) => (
                      <MenuItem key={mt} value={mt}>{MEDICATION_TYPE_LABELS[mt]}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Meeting Flow</Typography>
              <Select
                multiple
                fullWidth
                size="small"
                value={flowIds}
                onChange={(e) => handleFlowSelect(e.target.value as number[])}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Typography variant="body2" color="text.secondary">
                    {(selected as number[]).length} meeting type(s) selected
                  </Typography>
                )}
                sx={CLINIC_DIALOG_SX.select}
              >
                {meetingTypes.map((mt) => (
                  <MenuItem key={mt.id} value={mt.id}>
                    <Checkbox checked={flowIds.includes(mt.id)} />
                    <ListItemText primary={mt.name} secondary={mt.only_once ? 'once' : 'repeatable'} />
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {flowIds.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: { sm: '160px' } }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={flowIds} strategy={verticalListSortingStrategy}>
                    {flowIds.map((id, index) => {
                      const mt = meetingTypeById.get(id);
                      return mt ? (
                        <FlowRow
                          key={id}
                          meetingType={mt}
                          index={index}
                          onRemove={() => setFlowIds((prev) => prev.filter((x) => x !== id))}
                        />
                      ) : null;
                    })}
                  </SortableContext>
                </DndContext>
                <Typography variant="caption" color="text.secondary">
                  Drag to reorder. The first step without a completed meeting is the patient's next meeting;
                  repeatable types stay due once reached, so put them last.
                </Typography>
              </Box>
            )}
          </Box>
        </ClinicDialog>

        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => crud.handleDelete(serviceToDelete)}
          entityType="Service"
          entityName={serviceToDelete?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default ServicesPage;
